// ============================================================
// Bildirimler (§2)
//
// Bildirimler VERİDEN TÜRETİLİR, ayrı koleksiyonda saklanmaz.
// Sebebi: saklanan bildirim bayatlar. Fatura ödendiğinde "gecikti"
// bildiriminin de kaybolması gerekir; ayrı kayıt tutulursa iki yeri
// birden güncellemek gerekir ve biri kaçınılmaz olarak unutulur.
//
// Saklanan iki şey var:
//   1. Okundu/kapatıldı durumu (anahtar bazlı)
//   2. Gerçekten gönderilmiş mesajların kaydı — "veliye haber verildi
//      mi?" sorusunun geçmişe dönük cevabı
// ============================================================

import { BUGUN, DONEM } from "../config.js";
import { KANALLAR, ONEM } from "../data/tanimlar.js";
import { kayitliBildirimListesi } from "./bildirim-kayitlari.js";
import { B, subeBaglaminda, subeBulHerYerde, subeler } from "./context.js";
import { cocuklarim, rol } from "./auth.js";
import { faturaDurumu, faturaTutari, tahsilEdilen } from "./invoices.js";
import { belgeOzeti } from "./documents.js";
import { bildirilmemisOlaylar, ogrencininOlaylari, suresiSorunluIlaclar } from "./health.js";
import { eksikRizalar } from "./kvkk.js";
import { kontenjan } from "../views/basvurular.js";

export { KANALLAR, ONEM };

const GELMEDI_SAATI = "09:30";


const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());

// ---------- Kanallar ----------

export const kanallar = branchId => sube(branchId).notificationChannels ?? { uygulama: true };

export const kanalAcik = (kanal, branchId) => Boolean(kanallar(branchId)[kanal]);

/** Kanal açık ama entegrasyonu kurulmamış olanlar. */
export const kurulmayanKanallar = branchId =>
  Object.entries(kanallar(branchId))
    .filter(([k, acik]) => acik && !KANALLAR[k]?.hazir)
    .map(([k]) => k);

// ---------- Türetilen bildirimler ----------
//
// Her tetikleyici bir kitleye bakar. `veli` kitlesindekiler yalnızca
// o velinin çocuğuyla ilgili olanları görür.

const bildirim = (o) => ({ kanallar: ["uygulama"], onem: "bilgi", ...o });

/** Yönetim ve eğitmen tarafındaki bildirimler. */
function kurumBildirimleri(t) {
  const list = [];

  // --- Gelmeyen çocuk ---
  const gelmeyenler = t.students.filter(s => {
    const a = t.attendance[s.id];
    return a?.status === "gelmedi" && !a.reason;
  });
  for (const s of gelmeyenler) {
    list.push(bildirim({
      key: `gelmedi:${s.id}:${BUGUN}`,
      tip: "gelmedi",
      baslik: `${s.name} bugün gelmedi`,
      metin: `Saat ${GELMEDI_SAATI} itibarıyla giriş yapılmadı ve izin bildirilmedi. Veli aranmalı.`,
      onem: "uyari",
      kanallar: ["uygulama", "push", "sms"],
      at: `${BUGUN}T${GELMEDI_SAATI}`,
      hedef: `#/yoklama`,
      studentId: s.id,
    }));
  }

  // --- Kapanışa yakın hâlâ binada ---
  const icerde = t.students.filter(s => {
    const a = t.attendance[s.id];
    return a?.status === "geldi" && !a.out;
  });
  if (icerde.length && saatGecti("17:30")) {
    list.push(bildirim({
      key: `binada:${BUGUN}`,
      tip: "binada",
      baslik: `${icerde.length} çocuk hâlâ binada`,
      metin: "Kapanış saati yaklaştı. Çıkışı girilmemiş çocuklar var.",
      onem: "uyari",
      at: `${BUGUN}T17:30`,
      hedef: "#/yoklama",
    }));
  }

  // --- Veliye bildirilmemiş olaylar ---
  for (const o of bildirilmemisOlaylar(t.id)) {
    const s = t.students.find(x => x.id === o.studentId);
    list.push(bildirim({
      key: `olay:${o.id}`,
      tip: "olay",
      baslik: `${s?.name ?? "Öğrenci"} — olay velisine bildirilmedi`,
      metin: `${o.description} Bildirim bekletilmemeli.`,
      onem: "kritik",
      kanallar: ["uygulama", "push", "sms"],
      at: o.at,
      hedef: "#/saglik",
      studentId: o.studentId,
    }));
  }

  // --- Sorunlu ilaç kayıtları ---
  for (const m of suresiSorunluIlaclar(t.id)) {
    const s = t.students.find(x => x.id === m.studentId);
    list.push(bildirim({
      key: `ilac:${m.id}`,
      tip: "ilac",
      baslik: `${m.drugName} — son kullanma sorunu`,
      metin: `${s?.name ?? "Öğrenci"} için kayıtlı ilacın süresi ${m.expiresAt} tarihinde doluyor/doldu.`,
      onem: "kritik",
      at: `${BUGUN}T08:00`,
      hedef: "#/saglik",
    }));
  }

  // --- Belge süreleri ---
  const belge = belgeOzeti(t.id);
  if (belge.zorunluDolmus) {
    list.push(bildirim({
      key: `belge:dolmus:${DONEM}`,
      tip: "belge",
      baslik: `${belge.zorunluDolmus} zorunlu belgenin süresi doldu`,
      metin: "Denetimde doğrudan ceza sebebi. Yenilenmeli.",
      onem: "kritik",
      kanallar: ["uygulama", "eposta"],
      at: `${BUGUN}T08:00`,
      hedef: "#/ayarlar",
    }));
  } else if (belge.toplam) {
    list.push(bildirim({
      key: `belge:yaklasan:${DONEM}`,
      tip: "belge",
      baslik: `${belge.toplam} belgenin süresi yaklaşıyor`,
      metin: "60 gün içinde yenilenmesi gerekenler var.",
      onem: "uyari",
      kanallar: ["uygulama", "eposta"],
      at: `${BUGUN}T08:00`,
      hedef: "#/ayarlar",
    }));
  }

  // --- Eksik KVKK rızası ---
  const eksik = eksikRizalar(t.id);
  if (eksik.length) {
    list.push(bildirim({
      key: `riza:${DONEM}`,
      tip: "riza",
      baslik: `${eksik.length} öğrencide zorunlu rıza eksik`,
      metin: "Sağlık verisi işlenmemeli; evrak tamamlanmalı.",
      onem: "uyari",
      at: `${BUGUN}T08:00`,
      hedef: "#/ayarlar",
    }));
  }

  // --- Gecikmiş tahsilat ---
  const gecikmis = t.payments.filter(p => {
    const s = t.students.find(x => x.id === p.studentId);
    return s && faturaDurumu(s, p) === "gecikti";
  });
  if (gecikmis.length) {
    const tutar = gecikmis.reduce((a, p) => {
      const s = t.students.find(x => x.id === p.studentId);
      return a + faturaTutari(s, p) - tahsilEdilen(p);
    }, 0);
    list.push(bildirim({
      key: `tahsilat:${DONEM}`,
      tip: "tahsilat",
      baslik: `${gecikmis.length} fatura gecikmiş`,
      metin: `Toplam açık tutar ${tutar.toLocaleString("tr-TR")} ₺. Hatırlatma gönderilebilir.`,
      onem: "uyari",
      kanallar: ["uygulama", "eposta"],
      at: `${BUGUN}T08:00`,
      hedef: "#/odemeler",
    }));
  }

  // --- Dolan kontenjan ---
  for (const c of t.classes) {
    const k = kontenjan(c.id);
    if (k && k.bos <= 0) {
      list.push(bildirim({
        key: `kontenjan:${c.id}`,
        tip: "kontenjan",
        baslik: `${c.name} sınıfı doldu`,
        metin: `${k.dolu}/${k.kapasite}. Yeni başvurular bekleme listesine alınacak.`,
        onem: "bilgi",
        at: `${BUGUN}T08:00`,
        hedef: "#/basvurular",
      }));
    }
  }

  // --- Yarınki etkinlik ---
  const yarin = gunEkleBasit(BUGUN, 1);
  for (const e of t.events.filter(x => x.date === yarin)) {
    list.push(bildirim({
      key: `etkinlik:${e.date}:${e.title}`,
      tip: "etkinlik",
      baslik: `Yarın: ${e.title}`,
      metin: `${e.scope} · ${e.desc}`,
      onem: "bilgi",
      kanallar: ["uygulama", "push"],
      at: `${BUGUN}T16:00`,
      hedef: "#/etkinlikler",
    }));
  }

  return list;
}

/** Velinin göreceği bildirimler — yalnızca kendi çocuğuyla ilgili. */
function veliBildirimleri() {
  const list = [];

  for (const s of cocuklarim()) {
    subeBaglaminda(s.branchId, () => {
      const t = subeBulHerYerde(s.branchId);
      const a = t.attendance[s.id];
      const rapor = t.dailyReports.find(r => r.studentId === s.id);

      if (rapor) {
        list.push(bildirim({
          key: `rapor:${s.id}:${BUGUN}`,
          tip: "rapor",
          baslik: `${s.name} — günün raporu hazır`,
          metin: `Öğle: ${rapor.meals.ogle} · Uyku: ${rapor.sleep} · Ruh hâli: ${rapor.mood}`,
          onem: "bilgi",
          kanallar: ["uygulama", "push"],
          at: `${BUGUN}T15:00`,
          hedef: "#/bugun",
        }));
      }

      if (a?.out && a.outBy) {
        list.push(bildirim({
          key: `cikis:${s.id}:${BUGUN}`,
          tip: "cikis",
          baslik: `${s.name} teslim edildi`,
          metin: `${a.out} · ${a.outBy.name} (${a.outBy.relation}) teslim aldı.`,
          onem: "bilgi",
          kanallar: ["uygulama", "push"],
          at: `${BUGUN}T${a.out}`,
          hedef: "#/bugun",
        }));
      }

      for (const o of ogrencininOlaylari(s.id, s.branchId).filter(x => x.at.startsWith(BUGUN))) {
        list.push(bildirim({
          key: `veliolay:${o.id}`,
          tip: "olay",
          baslik: `${s.name} — ${o.location}`,
          metin: `${o.description} Yapılan: ${o.firstAid}`,
          onem: "kritik",
          kanallar: ["uygulama", "push", "sms"],
          at: o.at,
          hedef: "#/bugun",
        }));
      }

      const fatura = t.payments.find(p => p.studentId === s.id && p.period === DONEM);
      if (fatura && faturaDurumu(s, fatura) !== "odendi") {
        const kalan = faturaTutari(s, fatura) - tahsilEdilen(fatura);
        list.push(bildirim({
          key: `velifatura:${s.id}:${DONEM}`,
          tip: "fatura",
          baslik: `${s.name} — ödeme hatırlatması`,
          metin: `${kalan.toLocaleString("tr-TR")} ₺ · son ödeme ${fatura.due}`,
          onem: fatura.due < BUGUN ? "uyari" : "bilgi",
          kanallar: ["uygulama", "eposta"],
          at: `${BUGUN}T09:00`,
          hedef: "#/odemelerim",
        }));
      }
    });
  }

  return list;
}

// ---------- Genel API ----------

/**
 * Oturumdaki kullanıcının bildirimleri — önemli ve yeni olan üstte.
 * Okunmuşlar listede kalır ama işaretlidir; kapatılanlar düşer.
 */
export function bildirimler({ okunanlarDahil = true } = {}) {
  const r = rol();
  if (!r) return [];

  // Sistem operatörüne kurum bildirimi ÜRETİLMEZ. Bunlar çocuk adı
  // taşıyor ("Kuzey bugün gelmedi"), uygulama sahibinin göreceği şey
  // değil. Arayüzde zil zaten gizli; burada da kesiyoruz ki başka bir
  // çağıran (rozet, sayaç, ileride e-posta özeti) sızdırmasın.
  if (r === "sistem") return [];

  const turetilen = r === "veli" ? veliBildirimleri() : kurumBildirimleri(B());

  // Sunucunun ürettiği kalıcı kayıtlar (zamanlanmış işler). Aynı
  // olayın hem türetilmişi hem kayıtlısı olabilir; anahtar üzerinden
  // tekilleştiriyoruz ve KAYITLI olanı tercih ediyoruz — okundu
  // durumu onda kalıcı, türetilmişte yalnızca bu cihazda.
  const kayitli = kayitliBildirimListesi();
  const kayitliAnahtarlar = new Set(kayitli.map(k => k.key));
  const ham = [...kayitli, ...turetilen.filter(b => !kayitliAnahtarlar.has(b.key))];

  const durumlar = B().notificationRead ?? {};

  return ham
    .map(b => ({
      ...b,
      okundu: Boolean(b.okundu) || Boolean(durumlar[b.key]?.readAt),
      kapatildi: Boolean(durumlar[b.key]?.dismissedAt),
    }))
    .filter(b => !b.kapatildi)
    .filter(b => okunanlarDahil || !b.okundu)
    .sort((a, b) => {
      const o = ONEM[b.onem].sira - ONEM[a.onem].sira;
      return o !== 0 ? o : b.at.localeCompare(a.at);
    });
}

export const okunmamisSayisi = () => bildirimler({ okunanlarDahil: false }).length;

// ---------- Gönderim kaydı ----------

export const gonderimler = branchId => sube(branchId).notificationSends ?? [];

/** Bir bildirim için daha önce gönderim yapılmış mı? */
export const gonderildiMi = (key, branchId) =>
  gonderimler(branchId).some(g => g.key === key);

export const gonderimSayisi = (key, branchId) =>
  gonderimler(branchId).filter(g => g.key === key).length;

/** Bir bildirimin hangi kanallardan gönderilebileceği. */
export function gonderilebilirKanallar(b, branchId) {
  return (b.kanallar ?? []).filter(k => k !== "uygulama" && kanalAcik(k, branchId));
}

// ---------- Yardımcılar ----------

function saatGecti(saat) {
  const simdi = new Date();
  const [h, d] = saat.split(":").map(Number);
  return simdi.getHours() > h || (simdi.getHours() === h && simdi.getMinutes() >= d);
}

function gunEkleBasit(iso, adim) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + adim);
  return d.toISOString().slice(0, 10);
}

/** Şube geneli bildirim özeti — panelde kullanılır. */
export function bildirimOzeti(branchId) {
  return subeBaglaminda(branchId ?? B().id, () => {
    const list = bildirimler();
    return {
      toplam: list.length,
      okunmamis: list.filter(b => !b.okundu).length,
      kritik: list.filter(b => b.onem === "kritik").length,
      uyari: list.filter(b => b.onem === "uyari").length,
    };
  });
}

/** Tüm şubelerdeki kritik bildirimler — süper admin için. */
export const kritikBildirimSayisi = () =>
  subeler().reduce((a, b) => a + bildirimOzeti(b.id).kritik, 0);
